import { getLocation, getWindowSafe } from '@change-corgi/core/window';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useFlyerDownloadModalContext } from 'src/app/pages/starterDashboardV2/shared/components';
import { useSharingModal } from 'src/app/pages/starterDashboardV2/shared/contexts';
import { useReadyToShare } from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { RecommendedGroup } from 'src/app/pages/starterDashboardV2/shared/types';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

import type { BaseUseSharingToolsReturn } from './base';
import { useBaseSharingTools } from './base';

export type { SharingPostData } from './base';

type SharingToolsActions = {
	navigateToAllTools: () => void;
	navigateToGroupRecommendations: () => void;
	navigateToReadyToSharePosts: () => void;
	navigateToShareVisual: () => void;
	openFlyerDownloadModal: () => void;
	navigateToEditPetition: () => void;
	openRTSFacebookSharingModal: (() => void) | undefined;
	openRTSInstagramSharingModal: (() => void) | undefined;
	openRecommendedFacebookGroupModal: (group: RecommendedGroup) => void;
	openRecommendedRedditGroupModal: (group: RecommendedGroup) => void;
};

export type UseSharingToolsReturn = Omit<BaseUseSharingToolsReturn, 'actions'> & { actions: SharingToolsActions };

const RTS_TRACKING_PROPS = {
	context: 'petition_dashboard_grow_petition_v2',
	viewContext: 'home',
} as const;

const RG_TRACKING_PROPS = {
	context: 'petition_dashboard_grow_petition_v2',
	viewContext: 'home',
} as const;

const CLICK_RTS_EVENT_NAME = 'petition_manage_ready_to_share_posts_click';
const CLICK_RG_EVENT_NAME = 'petition_manage_recommended_groups_click';
const CONTEXT = 'petition_dashboard_grow_petition_v2';
const VIEW_CONTEXT = 'home';

function trackRTSFacebookClick(track: ReturnType<typeof useTracking>) {
	void track(CLICK_RTS_EVENT_NAME, {
		context: CONTEXT,
		view_context: VIEW_CONTEXT,
		location: 'facebook_post',
	});
}

function trackRTSInstagramClick(track: ReturnType<typeof useTracking>) {
	void track(CLICK_RTS_EVENT_NAME, {
		context: CONTEXT,
		view_context: VIEW_CONTEXT,
		location: 'instagram_story',
	});
}

function trackRecommendedGroupClick(
	track: ReturnType<typeof useTracking>,
	groupName: string,
	platform: 'facebook' | 'reddit',
	groupSource: string,
) {
	const sourceKey = platform === 'facebook' ? 'fb_recommendation_source' : 'reddit_recommendation_source';
	void track(CLICK_RG_EVENT_NAME, {
		context: CONTEXT,
		view_context: VIEW_CONTEXT,
		location: `${platform}_group`,
		recommendation: groupName,
		[sourceKey]: groupSource,
	});
}

function navigateToRTSInstagramPosts(slug: string, navigate: ReturnType<typeof useNavigate>): void {
	const location = getLocation();
	const searchParams = new URLSearchParams(location.search);
	searchParams.set('platform', 'instagram');
	navigate(`/p/${slug}/dashboard/share?${searchParams.toString()}#ready-to-share-posts`);
}

export function useSharingTools(): UseSharingToolsReturn {
	const { data, actions } = useBaseSharingTools('petition_dashboard_grow_petition_v2');
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const { openModal: openFlyerDownloadModal } = useFlyerDownloadModalContext();
	const { startSharingFlow } = useSharingModal();
	const isMobile = useIsMobile();
	const navigate = useNavigate();
	const track = useTracking();
	const {
		data: { postTypeData },
	} = useReadyToShare();

	const facebookRTSPost = postTypeData.FACEBOOK_POST?.posts[0];
	const instagramRTSPost = postTypeData.INSTAGRAM_STORY?.posts[0];

	const openRecommendedFacebookGroupModal = (group: RecommendedGroup) => {
		const post = postTypeData.FACEBOOK_GROUPS_POST?.posts[0];
		trackRecommendedGroupClick(track, group.name, 'facebook', group.metadata.source);
		if (!post) {
			getWindowSafe()?.open(group.url, '_blank', 'noopener,noreferrer');
			return;
		}
		startSharingFlow({
			post,
			petition,
			recommendedGroup: group,
			trackingProps: RG_TRACKING_PROPS,
		});
	};

	const openRecommendedRedditGroupModal = (group: RecommendedGroup) => {
		const post = postTypeData.REDDIT_GROUPS_POST?.posts[0];
		trackRecommendedGroupClick(track, group.name, 'reddit', group.metadata.source);
		if (!post) {
			getWindowSafe()?.open(group.url, '_blank', 'noopener,noreferrer');
			return;
		}
		startSharingFlow({
			post,
			petition,
			recommendedGroup: group,
			trackingProps: RG_TRACKING_PROPS,
		});
	};

	return {
		data,
		actions: {
			...actions,
			openFlyerDownloadModal: () => {
				void track('petition_manage_grow_your_petition_click', {
					context: 'petition_dashboard_grow_petition_v2',
					content: 'poster',
					cta: 'button',
				});
				openFlyerDownloadModal('starter_dashboard_sharing_tools');
			},
			openRTSFacebookSharingModal: facebookRTSPost
				? () => {
						trackRTSFacebookClick(track);
						startSharingFlow({
							post: facebookRTSPost,
							petition,
							trackingProps: RTS_TRACKING_PROPS,
						});
					}
				: undefined,
			openRTSInstagramSharingModal: instagramRTSPost
				? () => {
						trackRTSInstagramClick(track);
						if (isMobile) {
							startSharingFlow({
								post: instagramRTSPost,
								petition,
								trackingProps: RTS_TRACKING_PROPS,
							});
						} else {
							navigateToRTSInstagramPosts(petition.slug, navigate);
						}
					}
				: undefined,
			openRecommendedFacebookGroupModal,
			openRecommendedRedditGroupModal,
		},
	};
}
